/** Defines the content access level */
export var AccessLevel;
(function (AccessLevel) {
    /** Private or hidden */
    AccessLevel["None"] = "NONE";
    /** Public */
    AccessLevel["Public"] = "PUBLIC";
    /** Restricted */
    AccessLevel["Restricted"] = "RESTRICTED";
})(AccessLevel || (AccessLevel = {}));
/** Defines how the assistant is rendered */
export var AssistantDisplayType;
(function (AssistantDisplayType) {
    /** Rendered as compressed */
    AssistantDisplayType["Compressed"] = "COMPRESSED";
    /** Rendered as full height */
    AssistantDisplayType["Full"] = "FULL";
    /** Rendered as full height and compressed */
    AssistantDisplayType["Pushin"] = "PUSHIN";
})(AssistantDisplayType || (AssistantDisplayType = {}));
/** Defines where on the page the tab should be displayed */
export var AssistantDockedPosition;
(function (AssistantDockedPosition) {
    /** Display as button */
    AssistantDockedPosition["Button"] = "BUTTON";
    /** Display on bottom of page */
    AssistantDockedPosition["Floor"] = "FLOOR";
    /** Display on the side of the page */
    AssistantDockedPosition["Wall"] = "WALL";
})(AssistantDockedPosition || (AssistantDockedPosition = {}));
/** Defines the side of the page where the assistant is placed */
export var AssistantSide;
(function (AssistantSide) {
    /** Placed on the left */
    AssistantSide["Left"] = "LEFT";
    /** Placed on the right */
    AssistantSide["Right"] = "RIGHT";
})(AssistantSide || (AssistantSide = {}));
/** Defines the theme used by the assistant */
export var AssistantTheme;
(function (AssistantTheme) {
    /** Dark theme */
    AssistantTheme["Dark"] = "DARK";
    /** Light theme */
    AssistantTheme["Light"] = "LIGHT";
})(AssistantTheme || (AssistantTheme = {}));
/** Defines what kind of element the helper refers to */
export var ElementEnumType;
(function (ElementEnumType) {
    /** Open article */
    ElementEnumType["Article"] = "ARTICLE";
    /** Open assistant */
    ElementEnumType["Assistant"] = "ASSISTANT";
    /** Display article element */
    ElementEnumType["ComponentArticle"] = "COMPONENT_ARTICLE";
    /** Display category element */
    ElementEnumType["ComponentCategory"] = "COMPONENT_CATEGORY";
    /** Display search element */
    ElementEnumType["ComponentSearch"] = "COMPONENT_SEARCH";
    /** Display suggestions element */
    ElementEnumType["ComponentSuggestion"] = "COMPONENT_SUGGESTION";
    /** Open module */
    ElementEnumType["Module"] = "MODULE";
})(ElementEnumType || (ElementEnumType = {}));
/** Describes possibles icons for this helper */
export var IndicatorTypeEnum;
(function (IndicatorTypeEnum) {
    /** No indicator */
    IndicatorTypeEnum["Nothing"] = "NOTHING";
    /** Question mark icon */
    IndicatorTypeEnum["QuestionMark"] = "QUESTION_MARK";
    /** Pulsating dot icon */
    IndicatorTypeEnum["Throbber"] = "THROBBER";
    /** Underline text */
    IndicatorTypeEnum["Underline"] = "UNDERLINE";
})(IndicatorTypeEnum || (IndicatorTypeEnum = {}));
/** Defines the KCS resolution/reply mode */
export var KcsMode;
(function (KcsMode) {
    /** Never show resolution mode */
    KcsMode["Never"] = "NEVER";
    /** Reply mode */
    KcsMode["Reply"] = "REPLY";
    /** Resolution mode */
    KcsMode["Resolve"] = "RESOLVE";
})(KcsMode || (KcsMode = {}));
/** Defines where a helper is opened */
export var MethodTypeEnum;
(function (MethodTypeEnum) {
    /** Open helper on the webpage directly */
    MethodTypeEnum["Inline"] = "INLINE";
    /** Open helper in the assistant */
    MethodTypeEnum["Tab"] = "TAB";
})(MethodTypeEnum || (MethodTypeEnum = {}));
/** Defines the content restriction level */
export var RestrictionLevel;
(function (RestrictionLevel) {
    /** Restricted */
    RestrictionLevel["Restricted"] = "RESTRICTED";
    /** Unrestricted */
    RestrictionLevel["Unrestricted"] = "UNRESTRICTED";
})(RestrictionLevel || (RestrictionLevel = {}));
export const ArticleFullFragmentDoc = `
    fragment ArticleFull on ArticleDetail {
  id
  title
  slug
  summary
  body
  bodyText
  keywords
  accessControl {
    access
    isInternal
    smartGroups {
      id
      name
    }
  }
  featuredImageUrl
  createdAt
  lastUpdatedAt
  lastPublishedAt
  author {
    id
    name
    avatar
  }
  wordCount
}
    `;
export const CategoryPartialFragmentDoc = `
    fragment CategoryPartial on CategoryListResult {
  id
  title
  slug
  featuredImageUrl
}
    `;
export const ArticlePartialFragmentDoc = `
    fragment ArticlePartial on ArticleListResult {
  id
  title
  slug
  summary
  lastPublishedAt
}
    `;
export const ArticleResultFragmentDoc = `
    fragment ArticleResult on ArticleListResultSet {
  results {
    ...ArticlePartial
  }
  pageInfo {
    totalCount
  }
}
    ${ArticlePartialFragmentDoc}`;
export const CategoryFullFragmentDoc = `
    fragment CategoryFull on CategoryListResult {
  ...CategoryPartial
  articles(paging: {first: 1000, offset: 0}) {
    ...ArticleResult
  }
  parentCategory {
    ...CategoryPartial
    articles(paging: {first: 1000, offset: 0}) {
      ...ArticleResult
    }
  }
  subCategories {
    ...CategoryPartial
    articles(paging: {first: 1000, offset: 0}) {
      ...ArticleResult
    }
  }
}
    ${CategoryPartialFragmentDoc}
${ArticleResultFragmentDoc}`;
export const ArticlePageDocument = `
    query ArticlePage($articleId: ID!, $pageUrl: URL!, $languageId: LanguageID!) {
  article(id: $articleId, pageUrl: $pageUrl, languageId: $languageId) {
    ...ArticleFull
    category {
      ...CategoryFull
    }
  }
  relatedArticles: articles(
    pageUrl: $pageUrl
    languageId: $languageId
    query: {paging: {first: 5, offset: 0}, similarTo: [$articleId]}
  ) {
    results {
      id
      title
      slug
      summary
      lastPublishedAt
      category {
        id
      }
    }
    pageInfo {
      totalCount
    }
  }
}
    ${ArticleFullFragmentDoc}
${CategoryFullFragmentDoc}`;
export const SearchDocument = `
    query Search($query: ArticleSearchQuery!, $pageUrl: URL!, $languageId: LanguageID!) {
  searchArticles(query: $query, pageUrl: $pageUrl, languageId: $languageId) {
    pageInfo {
      totalCount
      currentPage
      totalPages
    }
    results {
      id
      title
      slug
      lastPublishedAt
      summary
      highlights {
        title
        body
      }
      category {
        id
        title
        slug
        parentCategory {
          id
          title
          slug
        }
      }
    }
  }
}
    `;
export const CategoryPageDocument = `
    query CategoryPage($categoryId: ID!, $pageUrl: URL!, $languageId: LanguageID!) {
  category(id: $categoryId, pageUrl: $pageUrl, languageId: $languageId) {
    ...CategoryFull
  }
}
    ${CategoryFullFragmentDoc}`;
export const CategoryTreeDocument = `
    query CategoryTree($pageUrl: URL!, $languageId: LanguageID!) {
  categories(pageUrl: $pageUrl, languageId: $languageId) {
    ...CategoryFull
  }
}
    ${CategoryFullFragmentDoc}`;
export const KnowledgebaseDocument = `
    query Knowledgebase($domain: String!) {
  knowledgebase(domain: $domain) {
    company {
      name
      uid
      defaultLanguage {
        id
      }
      languages {
        id
        englishName
        nativeName
      }
      whitelabelEnabled
    }
    settings {
      subdomain
      cname
      siteUrl
      searchEngineIndexed
      customDomainEnabled
      favicon
      httpsEnabled
      sso {
        enabled
        forced
        url
      }
      showEmbeddable
      searchAsYouTypeEnabled
      feedbackForm {
        enabled
        emailFieldHidden
      }
      liveVersion
      whitelistEnabled
      agentLoginEnabled
      companyManagedTls
    }
    redirects {
      fromUrl
      toUrl
    }
    accessWhitelist {
      cidrBlock
    }
    theme {
      id
      name
      liveVersion
    }
  }
}
    `;
export const SitemapDocument = `
    query Sitemap($pageUrl: URL!, $languageId: LanguageID!) {
  categories(pageUrl: $pageUrl, languageId: $languageId) {
    slug
    subCategories {
      slug
      articles(paging: {first: 9999, offset: 0}) {
        results {
          slug
        }
      }
    }
    articles(paging: {first: 9999, offset: 0}) {
      results {
        slug
      }
    }
  }
}
    `;
export const UserSettingsDocument = `
    query UserSettings($initPageLoad: Boolean!, $loggedInOnly: Boolean!, $pageUrl: URL!) {
  userSettings(
    initPageLoad: $initPageLoad
    loggedInOnly: $loggedInOnly
    pageUrl: $pageUrl
  ) {
    user {
      token
    }
    company {
      defaultLanguage {
        id
      }
      languages {
        id
        englishName
        nativeName
      }
    }
  }
}
    `;
export const UserTokenDocument = `
    query UserToken($initPageLoad: Boolean!, $loggedInOnly: Boolean!, $pageUrl: URL!, $enabled: Boolean!) {
  userSettings(
    initPageLoad: $initPageLoad
    loggedInOnly: $loggedInOnly
    pageUrl: $pageUrl
    enabled: $enabled
  ) {
    user {
      token
    }
  }
}
    `;
const defaultWrapper = (action, _operationName) => action();
export function getSdk(client, withWrapper = defaultWrapper) {
    return {
        ArticlePage(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(ArticlePageDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'ArticlePage');
        },
        Search(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(SearchDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'Search');
        },
        CategoryPage(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(CategoryPageDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'CategoryPage');
        },
        CategoryTree(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(CategoryTreeDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'CategoryTree');
        },
        Knowledgebase(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(KnowledgebaseDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'Knowledgebase');
        },
        Sitemap(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(SitemapDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'Sitemap');
        },
        UserSettings(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(UserSettingsDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'UserSettings');
        },
        UserToken(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(UserTokenDocument, variables, { ...requestHeaders, ...wrappedRequestHeaders }), 'UserToken');
        }
    };
}
//# sourceMappingURL=grappa.js.map